import { useState, useEffect, useCallback } from 'react';

// Masterclass API URL from environment variable (required for deployment)
// Uses dedicated masterclass API URL which points to the production backend
const MASTERCLASS_API_URL = process.env.REACT_APP_MASTERCLASS_API_URL || 'https://myeaitradingcompass.com';

export const useMasterclass = () => {
  const [masterclassData, setMasterclassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);

  // Capture referral code from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('referral_code', refCode);
      console.log('Referral code captured:', refCode);
    }
  }, []);

  // Fetch active masterclass on mount
  useEffect(() => {
    const fetchMasterclass = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${MASTERCLASS_API_URL}/api/public/masterclass`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch masterclass');
        }
        
        const data = await response.json();
        
        if (data.success && data.masterclass) {
          setMasterclassData(data.masterclass);
          console.log('Active masterclass loaded:', data.masterclass.title);
        } else {
          console.log('No active masterclass available');
          setMasterclassData(null);
        }
      } catch (err) {
        console.error('Failed to fetch masterclass:', err);
        setError(err.message);
        setMasterclassData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMasterclass();
  }, []);

  // Auto-show modal after 3 seconds (once per session)
  useEffect(() => {
    if (!masterclassData) return;
    
    const modalShown = sessionStorage.getItem('masterclass_modal_shown');
    if (!modalShown) {
      const timer = setTimeout(() => {
        setShowModal(true);
        sessionStorage.setItem('masterclass_modal_shown', 'true');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [masterclassData]);

  // Handle application submission
  const handleApply = useCallback(async (formData) => {
    try {
      // Get referral code from localStorage
      const referralCode = localStorage.getItem('referral_code');
      
      // Prepare payload according to API specification
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone, // digits only, no +
        country_code: formData.country_code, // e.g., "44", "1", "91" (no +)
        country: formData.country || '',
        trading_level: formData.trading_level, // Beginner | Intermediate | Advanced
        current_results: formData.current_results, // Profitable | Breakeven | Losing
        open_to_validation: formData.open_to_validation, // Yes | Maybe
        ...(referralCode && { referral_code: referralCode })
      };

      const response = await fetch(`${MASTERCLASS_API_URL}/api/public/masterclass/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Application failed');
      }
      
      const result = await response.json();
      
      // Close modal
      setShowModal(false);
      
      // Set application status
      setApplicationStatus(result.application_status);
      
      // Track conversion event
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'Masterclass Application',
          content_category: 'Trading Masterclass'
        });
      }

      // GTM event
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'masterclass_application',
          application_status: result.application_status
        });
      }
      
      // Show success toast
      setToast({
        message: result.message || "Application received! Your application is under review. You'll receive a WhatsApp confirmation shortly.",
        type: 'success'
      });
      
      return result;
    } catch (err) {
      console.error('Application error:', err);
      setToast({
        message: err.message || "Failed to submit application. Please try again.",
        type: 'error'
      });
      throw err;
    }
  }, []);

  // Open modal manually
  const openModal = useCallback(() => {
    setShowModal(true);
    sessionStorage.setItem('masterclass_modal_shown', 'true');
  }, []);

  // Close modal
  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  // Clear toast
  const clearToast = useCallback(() => {
    setToast(null);
  }, []);

  return {
    masterclassData,
    loading,
    error,
    showModal,
    toast,
    applicationStatus,
    openModal,
    closeModal,
    handleApply,
    clearToast
  };
};

export default useMasterclass;
